import { Injectable, inject } from '@angular/core';

import {
    CookieName,
    CookieService,
    GeolocationService as CoreGeolocationService,
    GeolocationPosition,
    NativeAppService,
    OnFeatureInit,
    TrackingService,
    UserLoginEvent,
    UserService,
} from '@frontend/vanilla/core';
import { delay, expand, filter, first, firstValueFrom, map, of, switchMap, take } from 'rxjs';

import { GeolocationConfig } from './geolocation.client-config';
import { GeolocationService } from './geolocation.service';

@Injectable()
export class GeolocationBootstrapService implements OnFeatureInit {
    private config = inject(GeolocationConfig);
    private geolocationService = inject(GeolocationService);
    private coreGeolocationService = inject(CoreGeolocationService);
    private user = inject(UserService);
    private trackingService = inject(TrackingService);
    private nativeAppService = inject(NativeAppService);
    private cookieService = inject(CookieService);

    async onFeatureInit() {
        await firstValueFrom(this.config.whenReady);

        this.geolocationService.clearPositionForGood();

        if (this.config.watchBrowserPositionOnAppStart) {
            this.geolocationService.watchBrowserPositionChanges();
        }

        this.geolocationService.restoreLastPositionFromCookie();
        this.geolocationService.watchNativePositionChanges();

        this.coreGeolocationService.set(this.geolocationService);

        this.trackGeolocationEventAfterLogin();
    }

    //Send Geolocation event to datalayer after login and location received, 3 times (to guarantee received), for CIP to trigger geo targetted offers.
    private trackGeolocationEventAfterLogin() {
        this.geolocationService.positionChanges
            .pipe(
                first(),
                switchMap((geolocation, index) => {
                    if (this.user.isAuthenticated) {
                        return of({ geolocation, index });
                    } else {
                        this.cookieService.remove(CookieName.VnGeolocationTracking);
                        return this.user.events.pipe(
                            filter((e) => e instanceof UserLoginEvent),
                            map(() => ({ geolocation, index })),
                        );
                    }
                }),
                expand(({ geolocation, index }) => of({ geolocation, index: index + 1 }).pipe(delay(500))),
                take(3),
            )
            .subscribe(({ geolocation, index }) => {
                const cookie = this.cookieService.get(CookieName.VnGeolocationTracking);
                if (!cookie && this.user.workflowType == 0) {
                    this.sendToGA(geolocation);
                }
                if (index == 2) {
                    this.cookieService.put(CookieName.VnGeolocationTracking, '1');
                }
            });
    }

    private sendToGA(geolocation: GeolocationPosition) {
        this.trackingService.triggerEvent('geolocation', {
            'user.profile.accountID': this.user.accountId,
            'native.nativeMode': this.nativeAppService.nativeMode,
            'GEO_LATITUDE': geolocation.coords.latitude,
            'GEO_LONGITUDE': geolocation.coords.longitude,
            'GEO_ALTITUDE': geolocation.coords.altitude,
            'GEO_ACCURACY': geolocation.coords.accuracy,
            'GEO_ALT_ACCURACY': geolocation.coords.altitudeAccuracy,
            'GEO_HEADING': geolocation.coords.heading,
            'GEO_SPEED': geolocation.coords.speed,
        });
    }
}
